import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { href: "/#about", label: "About" },
  { href: "/#services", label: "Services" },
  { href: "/commercial", label: "Commercial" },
  { href: "/residential", label: "Residential" },
  { href: "/#contact", label: "Contact" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if we should show transparent header (only on homepage at top)
  const showTransparent = isHomePage && !isScrolled;

  const NavLink = ({ href, label }: { href: string; label: string }) => {
    const isInternal = href.startsWith("/") && !href.includes("#");
    const isHashLink = href.includes("#");

    if (isInternal) {
      return (
        <Link
          to={href}
          className={`text-sm font-medium transition-colors hover:text-primary ${
            showTransparent ? "text-card/90 hover:text-card" : "text-muted-foreground"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        >
          {label}
        </Link>
      );
    }

    if (isHashLink && !isHomePage) {
      // On other pages, link back to home with hash
      return (
        <Link
          to={href}
          className={`text-sm font-medium transition-colors hover:text-primary ${
            showTransparent ? "text-card/90 hover:text-card" : "text-muted-foreground"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        >
          {label}
        </Link>
      );
    }

    return (
      <a
        href={href.replace("/#", "#")}
        className={`text-sm font-medium transition-colors hover:text-primary ${
          showTransparent ? "text-card/90 hover:text-card" : "text-muted-foreground"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {label}
      </a>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showTransparent
          ? "bg-transparent"
          : "bg-card/95 backdrop-blur-md shadow-md"
      }`}
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-16 md:h-20" aria-label="Main navigation">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading font-bold text-base lg:text-xl transition-colors ${
              showTransparent ? "text-card" : "text-foreground"
            }`}
          >
            Hill Brothers
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink key={link.href} {...link} />
            ))}
            <a
              href="tel:+16034244608"
              className={`flex items-center gap-2 font-semibold transition-colors ${
                showTransparent ? "text-card hover:text-card/80" : "text-accent hover:text-accent/80"
              }`}
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              603 424 4608
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${
              showTransparent ? "text-card" : "text-foreground"
            }`}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-card rounded-lg shadow-lg p-4 mb-4">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => {
                const isInternal = link.href.startsWith("/") && !link.href.includes("#");
                
                if (isInternal) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-foreground font-medium py-2 px-3 rounded-md hover:bg-secondary transition-colors"
                    >
                      {link.label}
                    </Link>
                  );
                }

                return (
                  <a
                    key={link.href}
                    href={isHomePage ? link.href.replace("/#", "#") : link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-foreground font-medium py-2 px-3 rounded-md hover:bg-secondary transition-colors"
                  >
                    {link.label}
                  </a>
                );
              })}
              <a
                href="tel:+16034244608"
                className="flex items-center gap-2 text-accent font-semibold py-2 px-3"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                603 424 4608
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
